import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useCallback, useEffect, useState } from 'react'
import { Kap_Journal, Query_Root } from 'src/@types/graphql'
import { usePermissionsForKapProgram } from 'src/service/security/PermissionHook'
import { useParams } from 'react-router'
import { queryKapJournalsPrimaryInfoByCantonCode } from 'src/screens/kap/program/journal/kapJournalQueries'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { ProgramExportMenu } from 'src/shared/menu/ProgramExportMenu'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { AddButton } from 'src/shared/button/Buttons'
import { Section } from 'src/shared/presentation/Section'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Stack } from '@mui/material'
import { ExpandableKapJournalCard } from 'src/screens/kap/program/journal/journal-card/ExpandableKapJournalCard'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { queryKapProgramCantonCode } from 'src/screens/kap/program/details/kapProgramQueries'

export const KapProgramJournalPage = () => {
  const { programId } = useParams()
  const programIdInt = parseInt(programId as string)
  const { getMessage } = useMessageSource()

  const urqlClient = useClient()

  const navigate = useDelayedNavigate()
  const notificationService = useNotificationService()

  const [journals, setJournals] = useState<Kap_Journal[]>()

  const { canViewJournals, canEditJournals, loading } = usePermissionsForKapProgram(programIdInt)

  const fetchCantonData = useCallback(async (): Promise<string> => {
    const { data } = await urqlClient
      .query<{ kap_program_by_pk: Query_Root['kap_program_by_pk'] }>(queryKapProgramCantonCode, {
        kap_program_id: programIdInt,
      })
      .toPromise()

    const cantonCode = data?.kap_program_by_pk?.canton_code
    if (!cantonCode) {
      throw new Error('Resource not found')
    }

    return cantonCode
  }, [programIdInt, urqlClient])

  const fetchJournalData = useCallback(
    async (cantonCode: string) => {
      const { data } = await urqlClient
        .query<{ kap_journal: Query_Root['kap_journal'] }>(queryKapJournalsPrimaryInfoByCantonCode, {
          canton_code: cantonCode,
        })
        .toPromise()

      if (!data?.kap_journal) {
        throw new Error('Resource not found')
      }

      return data.kap_journal
    },
    [urqlClient],
  )

  const fetchAllData = useCallback(() => {
    fetchCantonData()
      .then((cantonData) => fetchJournalData(cantonData))
      .then((journalData) => setJournals(journalData))
      .catch((_) => notificationService.operationFailed())
  }, [fetchCantonData, fetchJournalData, notificationService])

  useEffect(() => {
    if (programIdInt) {
      fetchAllData()
    }
  }, [programIdInt, fetchAllData])

  const handleAddJournalEntry = () => {
    navigate(ROUTES.KapProgramJournalNew.params({ programId }))
  }

  const handleEditJournalEntry = (journalId: number) => {
    navigate(ROUTES.KapProgramJournalEdit.params({ programId, journalId }))
  }

  if (!loading && !canViewJournals) {
    return <NotAuthorized />
  }

  return (
    <ScreenLayout
      title={getMessage('label.navigation.program.journal')}
      actions={<ProgramExportMenu />}
      hasSecondLevelNavigation={false}
    >
      <PageLayout>
        {!loading && (
          <Section
            title={getMessage('label.program.journal.entries')}
            actionButton={
              <AddButton messageKey={getMessage('button.add.program.journal')} onClick={handleAddJournalEntry} />
            }
            helpAndInstructions={<HelpAndInstructions labelKey="label.help.program.journal" />}
          >
            {journals?.length && (
              <Stack spacing={1}>
                {journals.map((journal) => {
                  return (
                    <ExpandableKapJournalCard
                      key={journal.id}
                      id={String(journal.id)}
                      kapJournal={journal}
                      canEdit={canEditJournals}
                      onEdit={() => handleEditJournalEntry(journal.id)}
                      refetch={fetchAllData}
                    />
                  )
                })}
              </Stack>
            )}
          </Section>
        )}
      </PageLayout>
    </ScreenLayout>
  )
}
